import React, { useState, useEffect, useCallback, useRef } from "react";
import "./GameTypesGrid.css"; // Add styling here if necessary
import GameGrid from "./GamesGrid"; // Import the GamesGrid component
import CategoryFillers from "./CategoryFillers";
import CobbleTrail from "./CobbleTrail";
import FlowerTurf from "./FlowerTurf";
import { useTrailGeometry } from "./trailGeometry";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const GameTypesGrid = ({
  imageWidth = 200,
  imageHeight = 140,
}) => {
  const [gameTypes, setGameTypes] = useState([]);
  const [gameCounts, setGameCounts] = useState({}); // { [typeId]: number of games }
  const bedsRef = useRef(null);

  // GamesGrid reports how many games it loaded; we use it to scale the fillers.
  const handleGamesCount = useCallback((typeId, count) => {
    setGameCounts((prev) => (prev[typeId] === count ? prev : { ...prev, [typeId]: count }));
  }, []);

  // Fetch game types from API
  useEffect(() => {
    fetch(`${SERVER_URL}/api/game-types/`)
      .then((response) => response.json())
      .then((data) => setGameTypes(data))
      .catch((error) => console.error("Error fetching game types:", error));
  }, []);

  // Bump whenever the rows change shape — categories arriving, or a category's
  // games loading (which grows its row) — so the trail geometry re-measures.
  const trailSignature = `${gameTypes.length}:${gameTypes
    .map((t) => gameCounts[t.game_type_id])
    .join(",")}`;

  // Measure the rows ONCE into the shared path geometry; the flowers (which keep
  // clear of the path) and the cobbles (which fill it) both render from it, so
  // they can never drift apart.
  const geometry = useTrailGeometry(bedsRef, trailSignature);

  return (
    <div
      className="garden-beds"
      ref={bedsRef}
      // Mirror the path's side inset (computed in the geometry) onto the content
      // insets so the categories pull in by the same amount the U does on wide
      // screens, leaving a matching strip of meadow around the outside.
      style={geometry ? { "--trail-edge": `${geometry.edge}px` } : undefined}
    >
      {/* Layer stack (all z 0, so DOM order paints back-to-front): the trail at
          the bottom, then the hero's torn ridge overhang so the path visibly
          slips UNDER its ragged edge, then the flower turf so blooms ride over
          both. Flowers and stones never overlap anyway (the scatter keeps every
          bloom clear of the path corridor), so the trail sitting below the turf
          costs nothing. The category plots (z 1) stay on top. */}
      {/* The cobblestone trail, drawn as one fitted SVG ribbon. Even rows hug the
          left (category opens right), odd rows hug the right; it snakes between. */}
      <CobbleTrail geometry={geometry} />
      {/* The meadow-band overhang at the catalogue's top seam — positioned and
          painted by Home.css (it belongs to the catalogue frame), hosted here
          purely for its slot in this paint order. */}
      <div className="trail-catalogue__ridge" aria-hidden="true" />
      {/* The meadow the catalogue sits in: scattered wildflowers that keep clear
          of the path. */}
      <FlowerTurf geometry={geometry} />
      {gameTypes.map((gameType, index) => {
        // Even rows hug the left, odd rows hug the right — the trail and content
        // both align to this side, so categories visibly alternate down the page.
        const side = index % 2 === 0 ? "left" : "right";
        // Game count for this category (undefined until its games load); drives
        // how many filler props CategoryFillers scatters in the dead space.
        const count = gameCounts[gameType.game_type_id];
        return (
        <section
          key={gameType.game_type_id}
          className={`garden-bed garden-bed--${side}`}
        >
          {/* CategoryFillers deferred for later — placeholder art not ready yet.
          <CategoryFillers
            seedKey={gameType.game_type_id}
            gameCount={count}
            side={side}
          />
          */}
          {/* One cleared plot behind the whole category (tag + tiles) so each set
              of games sits in flower-free ground — see .garden-bed__plot in the CSS. */}
          <div className="garden-bed__plot">
            <div className="garden-bed__header">
              <div className="garden-bed__thumb">
                <img
                  src={`${SERVER_URL}${gameType.game_type_image}`}
                  alt={gameType.game_type_name}
                  className="garden-bed__image"
                />
              </div>
              <div className="garden-bed__heading">
                <h3 className="garden-bed__title heading-tertiary">{gameType.game_type_name} →</h3>
              </div>
            </div>
            <div className="garden-bed__carousel">
              <GameGrid
                gameTypeId={gameType.game_type_id}
                onGamesCount={(c) => handleGamesCount(gameType.game_type_id, c)}
              />
            </div>
          </div>
        </section>
        );
      })}
    </div>
  );
};

export default GameTypesGrid;
